import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { ProductCard } from "@/components/ProductCard";
import { ChevronRight } from "lucide-react";

export default function SuggestionsPage() {
  const [suggestions, setSuggestions] = useState([]);
  const [products, setProducts] = useState({});
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [loading, setLoading] = useState(true);

  const rideStyles = [
    { id: "Drifter", name: "Drifter", description: "Sideways style for the sideways life", color: "#39FF14" },
    { id: "Drag", name: "Drag", description: "Streamlined and focused", color: "#FF00FF" },
    { id: "Stance", name: "Stance", description: "Low and slow aesthetic", color: "#00FFFF" },
    { id: "Street", name: "Street", description: "The complete underground look", color: "#FFA500" }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [suggestionsRes, productsRes] = await Promise.all([
          axios.get(`${API}/suggestions`),
          axios.get(`${API}/products`)
        ]);
        setSuggestions(suggestionsRes.data);
        
        // Create a map of products by ID
        const productsMap = {};
        productsRes.data.forEach(p => {
          productsMap[p.id] = p;
        });
        setProducts(productsMap);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredSuggestions = selectedStyle
    ? suggestions.filter(s => s.ride_style === selectedStyle)
    : suggestions;

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="suggestions-page">
      {/* Hero */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=1920&q=80"
            alt="The Garage"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-[#030303]/80 to-[#030303]"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <span className="label-text text-[#FF00FF] text-sm tracking-[0.3em]">PIT CREW PICKS</span>
          <h1 className="mt-4 text-5xl sm:text-6xl heading text-[#EDEDED]">
            THE <span className="text-[#39FF14] neon-text">GARAGE</span>
          </h1>
          <p className="mt-6 text-lg text-[#888] max-w-2xl mx-auto">
            Curated looks based on your ride style. Find your perfect fit 
            and represent your lane.
          </p>
        </div>
      </section>

      {/* Ride Style Selector */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 border-y border-[#222]">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-center text-[#888] label-text mb-8">What's Your Ride Style?</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setSelectedStyle(null)}
              className={`px-6 py-3 border transition-colors label-text ${
                !selectedStyle 
                  ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" 
                  : "border-[#333] text-[#888] hover:border-[#888]"
              }`}
              data-testid="style-all"
            >
              All Styles
            </button>
            {rideStyles.map((style) => (
              <button
                key={style.id}
                onClick={() => setSelectedStyle(style.id)}
                className={`px-6 py-3 border transition-colors label-text ${
                  selectedStyle === style.id 
                    ? `border-[${style.color}] text-[${style.color}]` 
                    : "border-[#333] text-[#888] hover:border-[#888]"
                }`}
                style={{
                  borderColor: selectedStyle === style.id ? style.color : undefined,
                  color: selectedStyle === style.id ? style.color : undefined,
                  backgroundColor: selectedStyle === style.id ? `${style.color}10` : undefined
                }}
                data-testid={`style-${style.id.toLowerCase()}`}
              >
                {style.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Suggestions Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-video skeleton rounded-lg"></div>
              ))}
            </div>
          ) : filteredSuggestions.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#888]">No suggestions found for this style.</p>
            </div>
          ) : (
            <div className="space-y-24">
              {filteredSuggestions.map((suggestion, index) => {
                const style = rideStyles.find(s => s.id === suggestion.ride_style);
                const suggestionProducts = suggestion.products
                  .map(id => products[id])
                  .filter(Boolean);

                return (
                  <div 
                    key={suggestion.id}
                    className={`grid grid-cols-1 lg:grid-cols-2 gap-12 ${
                      index % 2 === 1 ? "lg:flex-row-reverse" : ""
                    }`}
                    data-testid={`suggestion-${suggestion.id}`}
                  >
                    {/* Image */}
                    <div className={`relative overflow-hidden ${index % 2 === 1 ? "lg:order-2" : ""}`}>
                      <div className="aspect-[4/3] relative">
                        <img
                          src={suggestion.image_url}
                          alt={suggestion.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-transparent"></div>
                        <div 
                          className="absolute top-4 left-4 px-4 py-2"
                          style={{ backgroundColor: style?.color }}
                        >
                          <span className="label-text text-black">{suggestion.ride_style}</span>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className={`flex flex-col justify-center ${index % 2 === 1 ? "lg:order-1" : ""}`}>
                      <h2 className="text-3xl heading text-[#EDEDED]">{suggestion.title}</h2>
                      <p className="mt-4 text-[#888] text-lg">{suggestion.description}</p>

                      {/* Products in this suggestion */}
                      {suggestionProducts.length > 0 && (
                        <div className="mt-8">
                          <h4 className="text-[#888] label-text mb-4">Included in this look:</h4>
                          <div className="space-y-4">
                            {suggestionProducts.map((product) => (
                              <Link
                                key={product.id}
                                to={`/product/${product.id}`}
                                className="flex items-center gap-4 p-3 bg-[#0A0A0A] border border-[#222] hover:border-[#39FF14]/50 transition-colors group"
                              >
                                <img
                                  src={product.image_url}
                                  alt={product.name}
                                  className="w-16 h-16 object-cover"
                                />
                                <div className="flex-1">
                                  <p className="text-[#EDEDED] group-hover:text-[#39FF14] transition-colors">
                                    {product.name}
                                  </p>
                                  <p className="text-[#39FF14] heading">${product.price.toFixed(2)}</p>
                                </div>
                                <ChevronRight size={20} className="text-[#888] group-hover:text-[#39FF14]" />
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Shop the look CTA */}
                      <Link
                        to="/shop"
                        className="mt-8 inline-flex btn-skew bg-[#39FF14] text-black px-8 py-4 font-bold uppercase self-start"
                        style={{ backgroundColor: style?.color }}
                      >
                        <span>Shop This Style</span>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto text-center">
          <span className="label-text text-[#00FFFF] text-sm tracking-[0.3em]">CAN'T DECIDE?</span>
          <h2 className="mt-4 text-4xl heading text-[#EDEDED]">Browse All Products</h2>
          <p className="mt-4 text-[#888] max-w-2xl mx-auto">
            Mix and match to create your own unique style. No rules, just vibes.
          </p>
          <Link
            to="/shop"
            className="mt-8 inline-flex btn-skew bg-[#39FF14] text-black px-8 py-4 font-bold uppercase"
            data-testid="browse-all-btn"
          >
            <span>Shop All Products</span>
          </Link>
        </div>
      </section>
    </div>
  );
}
